/**
 * Snapshot Comparison module: compares latest snapshot to previous snapshot.
 * Highlights audit readiness, compliance score, document completeness,
 * vehicle readiness, and expiration health.
 */
import React from 'react';
import { Card, CardHeader, CardBody } from '../ui';
import { formatAnalyticsDocumentDisplay } from '../../utils/analyticsDocumentLabel';

const SCORE_LABELS = {
  auditReadinessScore: 'Audit readiness',
  complianceScore: 'Compliance score',
  documentCompletenessScore: 'Document completeness',
  vehicleReadinessScore: 'Vehicle readiness',
  authorityHealthScore: 'Authority health',
  expirationRiskScore: 'Expiration health',
};

function deltaView(current, previous) {
  const curr = current != null ? Number(current) : null;
  const prev = previous != null ? Number(previous) : null;
  if (curr == null && prev == null) return null;
  if (prev == null) {
    return {
      valueText: `${Math.round(curr ?? 0)}%`,
      firstSnapshotNote: 'First snapshot',
      changePill: null,
    };
  }
  const currVal = curr ?? 0;
  const prevVal = prev ?? 0;
  const delta = Math.round(currVal - prevVal);
  if (delta > 0) {
    return {
      valueText: `${Math.round(currVal)}%`,
      firstSnapshotNote: null,
      changePill: { kind: 'up', text: `↑ +${delta}` },
    };
  }
  if (delta < 0) {
    return {
      valueText: `${Math.round(currVal)}%`,
      firstSnapshotNote: null,
      changePill: { kind: 'down', text: `↓ -${Math.abs(delta)}` },
    };
  }
  return {
    valueText: `${Math.round(currVal)}%`,
    firstSnapshotNote: null,
    changePill: { kind: 'neutral', text: 'No change' },
  };
}

export default function SnapshotComparisonModule({ snapshots, loading }) {
  const latest = snapshots?.[0];
  const previous = snapshots?.[1];

  if (loading) {
    return (
      <Card className="analytics-card snapshot-comparison-module">
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Comparing snapshots...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!latest) {
    return (
      <Card className="analytics-card snapshot-comparison-module">
        <CardHeader title="Snapshot comparison" subtitle="Latest vs previous" />
        <CardBody>
          <p className="analytics-empty-message">No snapshots yet. After the next daily run, you'll see how your metrics compare.</p>
        </CardBody>
      </Card>
    );
  }

  const audit = deltaView(latest.auditReadinessScore, previous?.auditReadinessScore);
  const compliance = deltaView(latest.complianceScore, previous?.complianceScore);
  const docs = deltaView(latest.documentCompletenessScore, previous?.documentCompletenessScore);
  const vehicle = deltaView(latest.vehicleReadinessScore, previous?.vehicleReadinessScore);
  const authority = deltaView(latest.authorityHealthScore, previous?.authorityHealthScore);
  const expiration = deltaView(latest.expirationRiskScore, previous?.expirationRiskScore);

  const rows = [
    audit && { label: SCORE_LABELS.auditReadinessScore, ...audit },
    compliance && { label: SCORE_LABELS.complianceScore, ...compliance },
    docs && { label: SCORE_LABELS.documentCompletenessScore, ...docs },
    vehicle && { label: SCORE_LABELS.vehicleReadinessScore, ...vehicle },
    authority && { label: SCORE_LABELS.authorityHealthScore, ...authority },
    expiration && { label: SCORE_LABELS.expirationRiskScore, ...expiration },
  ].filter(Boolean);

  const displayValue = (v) => {
    const s = v == null ? '' : String(v);
    if (!s || /^-?\d/.test(s.trim()) || /%/.test(s) || /[↑↓]/.test(s)) return s;
    return formatAnalyticsDocumentDisplay(s) || s;
  };

  return (
    <Card className="analytics-card snapshot-comparison-module">
      <CardHeader
        title="Snapshot comparison"
        subtitle="Latest vs previous — see what changed"
      />
      <CardBody>
        <p className="snapshot-comparison-intro">
          Here's how your key metrics compare to the last snapshot. Small moves in the right direction add up.
        </p>
        <dl className="snapshot-comparison-list">
          {rows.map((row) => (
            <div key={row.label} className="snapshot-comparison-row">
              <dt>{row.label}</dt>
              <dd className="snapshot-comparison-delta snapshot-comparison-delta--pills">
                <span className="snapshot-comparison-value">{displayValue(row.valueText)}</span>
                {row.firstSnapshotNote && (
                  <span className="snapshot-comparison-first-note">{displayValue(row.firstSnapshotNote)}</span>
                )}
                {row.changePill && (
                  <span className={`snapshot-change-pill snapshot-change-pill--${row.changePill.kind}`}>
                    {displayValue(row.changePill.text)}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </CardBody>
    </Card>
  );
}
